/**
 * Smart-TV / large-screen runtime detection.
 *
 * Tailwind breakpoints alone aren't enough on Android TV WebViews — most TVs
 * report a smaller CSS viewport (typically 960px or 1280px wide) than the
 * actual 1920×1080 panel, so `lg:` (≥1024px) sometimes fires and sometimes
 * doesn't depending on OEM scaling. We pick the platform up from a mix of
 * signals so the UI can always opt into the compact "TV layout".
 */

let _isTv = null;

function detect() {
  if (typeof window === "undefined") return false;
  // 1. Running inside the Android APK (Capacitor) → always treat as TV-style
  //    layout. The APK is the only place the player runs in production, so
  //    if a CSS-breakpoint-based phone layout looks too big on a TV, this
  //    catches it regardless of how the TV's WebView reports its viewport.
  if (window.Capacitor?.isNativePlatform?.()) return true;
  // 2. Physical screen width — survives DPI scaling on most Android TVs.
  if (window.screen?.width && window.screen.width >= 800) return true;
  // 3. User-Agent hints from Android TV / FireTV / GoogleTV WebViews.
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/android tv|googletv|smarttv|smart-tv|smart_tv|sony bravia|hbbtv|webos|tizen|aftn|aftt|aftb|firetv/.test(ua)) {
    return true;
  }
  // 4. CSS viewport width — last resort, also catches desktop monitors.
  if (window.innerWidth >= 1280) return true;
  return false;
}

/** Memoised on first call so every component sees the same value. */
export function isTvLayout() {
  if (_isTv === null) _isTv = detect();
  return _isTv;
}

/** Stamp the result onto <html data-tv="true"> so a stylesheet (or
 *  Tailwind's `data-[tv=true]:` variant) can target TVs declaratively. */
export function installTvDetection() {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.tv = isTvLayout() ? "true" : "false";
}
